import { SolutionPage } from "@/components/shared/SolutionPage";
import { MapPin, Star, Search, ArrowRight, Zap, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { SystemLadder } from "@/components/shared/SystemLadder";
import { ServiceFAQ } from "@/components/shared/ServiceFAQ";
import { LocalPackMockup } from "@/components/shared/LocalPackMockup";
import { ReviewsMockup } from "@/components/shared/ReviewsMockup";

const LocalSeoReputation = () => {
  return (
    <SolutionPage
      breadcrumbTitle="Local SEO & Reputation"
      seo={{
        title: "Local SEO & Reputation Management | Rank in Google Maps | Frayze",
        description: "Rank in Google Maps, dominate local search, and own your review layer. Get found by customers searching for what you do in your area.",
        keywords: "local SEO, Google Business Profile optimization, reputation management, local search ranking, Google Maps ranking, review management",
        canonical: "https://frayze.ca/solutions/local-seo-reputation/"
      }}
      hero={{
        badge: "Local Search Visibility System",
        badgeColor: "text-blue-600",
        title: "When customers search for what you do. Are you one of the top three?",
        description: "Most businesses are invisible when it matters most. We get you found in local search, dominate your map pack, and turn reviews into trust.",
        buttonColor: "bg-blue-600 hover:bg-blue-700",
        gradientParams: "from-blue-500/5 via-background to-background",
        primaryCta: { text: "See How Local Ranking Works", link: "#how-it-works" },
        secondaryCta: { text: "Book a Free 15-Min Call", link: "/book-consultation/" },
        visual: <LocalPackMockup />
      }}
      localStory={{
        eyebrow: "Local search example",
        client: "What customers see when they search",
        location: "Your local market",
        accentColor: "text-blue-600",
        story: (
          <>
            A homeowner types &ldquo;plumber near me&rdquo; into Google. Three local businesses appear in the map pack at the top. Those are the only three that get called. The fourth result, even if their website is beautiful, gets ignored. Local SEO is what decides which three show up.
          </>
        ),
        result: "If you're not one of those three, you're invisible to the buyer."
      }}
      problem={{
        title: "Most Local Businesses Are Invisible When Customers Search",
        description: (
          <>
            Your customers don&rsquo;t scroll past the first three results. They tap one, get a quote, and move on. If your business isn&rsquo;t there, you don&rsquo;t even get a chance to compete.
            <br /><br />
            <strong>Being on page 2 might as well be being closed.</strong>
          </>
        ),
        gridCols: 3,
        cards: [
          { value: "Top 3", label: "Local results take roughly 75% of all clicks. The rest fight over scraps." },
          { value: "4.5", label: "Minimum rating most customers check before trusting a local business." },
          { value: "91%", label: "Of customers read reviews before making a purchase decision." }
        ]
      }}

      systemModel={{
        title: "How Local Ranking Turns Into Booked Jobs",
        description: "Ranking is just the start. Here's how a search turns into a paying customer.",
        variant: "steps",
        connectionColor: "bg-gradient-to-r from-blue-200 via-blue-400 to-teal-400",
        items: [
          { step: "1", title: "Attract", description: "Dominate local search & maps to get found first.", icon: Search },
          { step: "2", title: "Validate", description: "Automated reviews build instant trust & authority.", icon: Star },
          { step: "3", title: "Verify", description: "Tracking proves leads come from local search, not vanity rankings.", icon: TrendingUp },
          { step: "4", title: "Feed", description: "Leads push instantly into your CRM pipeline.", icon: Zap }
        ]
      }}

      layers={{
        title: "What's Actually in the Build",
        description: "A strong Google profile, consistent citations, and a steady stream of 5-star reviews. That's the foundation.",
        theme: "blue",
        sidebarContent: <ReviewsMockup />,
        items: [
          {
            title: "Google Business Profile Optimization",
            icon: MapPin,
            items: [
              "Optimized profile that ranks in the map pack",
              "Service area set up correctly (not too broad)",
              "Posts, photos, Q&A managed & updated",
              "Review monitoring & professional replies"
            ]
          },
          {
            title: "Reviews on Autopilot + Reputation Layer",
            icon: Star,
            items: [
              "Auto-requests triggered after every job",
              "Reply templates for every review (positive & negative)",
              "Trust widgets embedded on your website",
              "Reputation reporting (leads, not just ratings)"
            ]
          },
          {
            title: "Citations & Local Schema",
            icon: MapPin,
            items: [
              "Consistent NAP (name, address, phone) across the web",
              "Citations built in industry directories",
              "Local schema markup on your website",
              "Content that targets local buyer searches"
            ]
          }
        ]
      }}

      interdependence={{
        title: "Ranking gets the click. Converting that click into a lead requires more than SEO.",
        description: (
          <>
            A strong Google profile and high reviews get you found. But if your website is slow, the form is broken, or the call goes to voicemail, you still lose. That&rsquo;s why we pair local SEO with a <Link to="/solutions/websites/" className="font-bold underline decoration-blue-500/50 hover:text-blue-600 transition-colors">conversion-focused website</Link> and plug it into your <Link to="/solutions/crm-automation/" className="font-bold underline decoration-teal-500/50 hover:text-teal-600 transition-colors">CRM follow-up system</Link>, so every lead you earn actually gets answered.
            <br /><br />
            For paid traffic on top of organic, see the <Link to="/solutions/lead-generation-system/" className="font-bold underline decoration-indigo-500/50 hover:text-indigo-600 transition-colors">Lead Generation System</Link>.
            <br /><br />
            Automate your entire review strategy with <Link to="/platform/reputation-management/" className="font-bold underline decoration-blue-500/50 hover:text-blue-600 transition-colors">Reputation Management</Link>.
            <br /><br />
            In a specific city? Start with our dedicated pages for <Link to="/service-areas/thunder-bay/" className="font-bold underline decoration-blue-500/50 hover:text-blue-600 transition-colors">SEO in Thunder Bay</Link> and <Link to="/service-areas/sudbury/" className="font-bold underline decoration-blue-500/50 hover:text-blue-600 transition-colors">SEO in Sudbury</Link>.
          </>
        ),
        icon: Zap,
        quote: "Get found. Get the call. Book the job. That's the whole point."
      }}

      useCases={{
        title: "Built to Win Local Customers in Your Industry",
        items: [
          { title: "Trades & Contractors", description: "Dominate 'plumber near me' searches with reviews that close deals.", link: "/industries/trades/" },
          { title: "Local Services", description: "Rank for buyer intent queries with proof (reviews) that converts.", link: "/solutions/" },
          { title: "Medical & Health", description: "Build authority with automated 5-star reviews and local presence.", link: "/solutions/" }
        ]
      }}

      socialProof={{
        title: "Built to be found and to convert",
        quote: "One system: a strong Google profile, consistent citations, and a review engine (measured by the leads it books, not just the rankings).",
        icon: Star,
        ctaLink: "/case-studies/",
        ctaText: "See real local SEO wins"
      }}

      cta={{
        title: "Get found by your next customer.",
        description: "We'll audit your local search position for free, then show you exactly how to break into the top three.",
        primaryCta: { text: "Get My Free Local SEO Audit", link: "/digital-audit/" },
        secondaryCta: { text: "Book a Free 15-Min Call", link: "/book-consultation/" },
        color: "bg-blue-600 hover:bg-blue-700"
      }}

      customSection={
        <div className="space-y-24 pb-24">
          {/* REPUTATION MANAGEMENT LINK */}
          <section className="container mx-auto px-6 max-w-4xl text-center">
            <div className="reveal lift-hover bg-primary/5 border border-primary/20 rounded-xl p-8">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Star className="w-5 h-5 text-primary" />
                <h3 className="text-xl font-bold">Own Your Review Layer</h3>
              </div>
              <p className="text-muted-foreground mb-6">
                Reviews are trust. Turn them into a system with our <strong>Reputation Management platform</strong>: auto-request, respond, and track the leads they generate.
              </p>
              <Button variant="outline" asChild>
                <Link to="/platform/reputation-management/">
                  Explore Reputation Management <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </section>

          {/* System Ladder */}
          <section className="container mx-auto px-6 pb-12">
            <div className="reveal text-center mb-12">
              <h2 className="text-2xl font-bold mb-4">Where This Fits in Your Growth System</h2>
              <p className="text-muted-foreground">Visibility is the layer that attracts. Everything else converts.</p>
            </div>
            <div className="reveal reveal-delay-1 bg-slate-50 dark:bg-slate-900/50 border rounded-2xl p-8 overflow-hidden max-w-5xl mx-auto">
              <SystemLadder activeTier="visibility" />
            </div>
          </section>

          <ServiceFAQ
            title="Questions About Local SEO & Reputation"
            description="Understanding ranking, reviews, and local search."
            items={[
              {
                question: "How long does it take to rank in the local map pack?",
                answer: "SEO is a marathon, not a sprint. Typically, you'll see significant movement in months 3-6. Results vary by competition, but our local domination strategy usually secures map pack placement faster than organic website ranking."
              },
              {
                question: "What's the difference between rankings and leads?",
                answer: "Rankings are vanity. We measure by leads (where someone comes from, such as local search or Google Maps) and whether they actually convert. A rank #1 with zero leads doesn't pay the bills. We track the full funnel: search to call to qualified lead."
              },
              {
                question: "Do reviews really matter?",
                answer: "Yes. 91% of customers read reviews before making a decision. A business with a 4.7-star rating gets clicked over a 4.2-star business every time. We automate review requests after every job so your rating builds constantly."
              },
              {
                question: "What if I already have negative reviews?",
                answer: "We manage it. Our strategy is to flood positive reviews so negatives become statistical noise. We respond professionally to all reviews (even negative ones). We also audit for fake reviews and remove them."
              },
              {
                question: "Will you guarantee #1 ranking?",
                answer: "No legitimate SEO company will. Google's algorithm changes constantly and competition varies. We guarantee a process: technical optimization, content strategy, citations, and reviews. Results follow. If someone promises guaranteed rankings, they're lying."
              },
              {
                question: "What if I want local SEO + a website redesign?",
                answer: "That's the play. Pair this with our Website Design service so your site is optimized to rank AND convert. Both systems feed each other. Ranking gets traffic, the website turns traffic into leads."
              }
            ]}
          />
        </div>
      }
    />
  );
};

export default LocalSeoReputation;
